import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IntlProvider } from 'react-intl';

import WaitingRoomView from '../components/WaitingRoomView';
import CaptchaContainer from './CaptchaContainer';
import withAsyncLifecycle from '../../core/hoc/async-lifecycle';
import translations from '../../core/i18n/translations';
import selectLocale from '../../core/selectors/i18n';
import { getSessionPosition, doVerifyToken } from '../actions/waiting-rooms';
import { setLocale } from '../../core/actions/i18n';
import { selectHttpInfo } from '../../core/selectors/http';
import { getWaitingRoomParamsFromUrl, handleSessionId } from '../utils/waiting-room-view-functions';
import { selectWaitingRoomViewInfo } from '../selectors/waiting-rooms';
import { GET_WAITING_ROOM_SESSION_POSITION_REQUEST_NAME, GET_WAITING_ROOMS_REQUEST_NAME } from '../api/config';

const WaitingRoomPage = () => {
    const dispatch = useDispatch();
    const locale = useSelector(selectLocale);
    const waitingRoomInfo = getWaitingRoomParamsFromUrl();
    const waitingRoom = useSelector((state) => selectWaitingRoomViewInfo(state, waitingRoomInfo.eventId));

    const [initialLoad, setInitialLoad] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [isCaptchaVerified, setIsCaptchaVerified] = useState(true);

    useEffect(() => {
        setSessionId(handleSessionId(waitingRoomInfo.eventId));
    }, []);

    useEffect(() => {
        if (!isCaptchaVerified) {
            return () => {};
        }

        if (waitingRoomInfo.language) {
            dispatch(setLocale(waitingRoomInfo.language));
        }

        if (!waitingRoomInfo.token) {
            setInitialLoad(true);
        }

        if ((!waitingRoom || Object.keys(waitingRoom).length === 0) && sessionId && waitingRoomInfo.token) {
            dispatch(doVerifyToken(waitingRoomInfo.token, sessionId, setInitialLoad));
        }

        return () => {};
    }, [sessionId]);

    useEffect(() => {
        if (!sessionId) {
            return () => {};
        }

        if (initialLoad) {
            setTimeout(() => dispatch(getSessionPosition(waitingRoomInfo.eventId, sessionId)), 1000);
        }

        const intervalId = setInterval(() => {
            dispatch(getSessionPosition(waitingRoomInfo.eventId, sessionId));
        }, 60000);

        return () => {
            clearInterval(intervalId);
        };
    }, [initialLoad, sessionId]);

    const { isFetching, isError } = useSelector((state) => selectHttpInfo(state, GET_WAITING_ROOMS_REQUEST_NAME));
    const { isFetching: isFetchingPosition, isError: isErrorPosition } = useSelector((state) => (
        selectHttpInfo(state, GET_WAITING_ROOM_SESSION_POSITION_REQUEST_NAME)
    ));

    const WaitingRoomViewWithLifecycle = withAsyncLifecycle(WaitingRoomView, {
        isFetching: (isFetchingPosition && !initialLoad) || (isFetching && !initialLoad) || !initialLoad,
        isError: isError || isErrorPosition,
    });

    return (
        <IntlProvider locale={locale} defaultLocale={locale} messages={translations[locale]}>
            {isCaptchaVerified
                ? <WaitingRoomViewWithLifecycle waitingRoom={waitingRoom} />
                : <CaptchaContainer setIsCaptchaVerified={setIsCaptchaVerified} />
            }
        </IntlProvider>
    );
};

export default WaitingRoomPage;
